import React from 'react';
import Select, { createFilter } from 'react-select';
import './Typeahead.scss';
import { FormGroup, ControlLabel } from 'react-bootstrap';
import { getClosestElement } from '../../utils/getClosestElement';
import { FixedSizeList as List } from "react-window";
import { useDirtyWarningContext, DirtyWarningInputProps } from '../DirtyWarning';

export type Option = {
    value: any, label: string
}

export type LabelMeta = {
    inputValue: string;
    selectValue: any;
}

export type SelectChangeActionMeta = { action: "select-option" | "deselect-option" | "remove-value" | "pop-value" | "set-value" | "clear" | "create-option" }

export type InputChangeActionMeta = { action: "set-value" | "input-change" | "input-blur" | "menu-close" }

export interface TypeaheadProps<T> extends DirtyWarningInputProps {
    label?: string;
    key?: string;
    id?: string;
    name: string;
    /** options that are id:name by default.  use getOptionLabel and getOptionValue to override id:name. */
    options?: T[];
    /** allows you to control the currently selected option via your state. */
    value?: T;
    /** control prop for the input value.  you might not need this, try controlling value instead. */
    inputValue?: string;
    placeholder?: string;
    multiple?: boolean;
    ref?: any;
    isLoading?: boolean;
    backspaceRemovesValue?: boolean;
    escapeClearsValue?: boolean;
    onChange: (selected: T, actionMeta?: SelectChangeActionMeta) => void;
    onBlur?: (e: any) => void;
    onFocus?: (e: any) => void;
    onInputChange?: (newValue: string, actionMeta?: InputChangeActionMeta) => void;
    getOptionLabel?: (option: T) => any;
    getOptionValue?: (option: T) => any;
    /** do you want to override how the menu items look? use this. metadata.context will give you menu or value. */
    renderMenuItemChildren?: (option: T, metadata?: LabelMeta) => React.ReactNode;
    isOptionSelected?: (option: any) => boolean;
};

class MenuList extends React.Component<any, any> {
    render() {
        let { options, children, maxHeight, getValue, getStyles } = this.props;
        const { width } = getStyles('menuList', this.props);
        const [value] = getValue();
        const itemSize = 35;
        const initialOffset = options.indexOf(value) * itemSize;

        return (
            <List
                height={maxHeight}
                width={width}
                itemCount={(children as any).length}
                itemSize={itemSize}
                initialScrollOffset={initialOffset}
            >
                {({ index, style }) => <div style={style}>{(children as any)[index]}</div>}
            </List>
        )
    }
}

export const Typeahead: React.FC<TypeaheadProps<any>> = (props) => {
    const context = useDirtyWarningContext();

    const { 
        onChange,
        onBlur,
        ref,
        label,
        renderMenuItemChildren,
        getOptionLabel,
        getOptionValue,
        name,
        escapeClearsValue,
        backspaceRemovesValue,
        disableDirtyTracking = false,
        multiple,
        ...otherProps } = props;
    const getOptionLabelFn = getOptionLabel || function (option: any) { return option.name };
    const getOptionValueFn = getOptionValue || function (option: any) { return option.id };

    //TODO: Replace react-bootstrap-typeahead.  This prevents the blur if we click on the dropdown menu's scrollbar.
    let wrapperOnBlur: any = undefined;
    if (onBlur != null) {
        wrapperOnBlur = function (e: any) {

            //all other browsers use event.relatedTarget
            //ie11 requires using document.activeElement for blurs
            const relatedTarget = e.relatedTarget || document.activeElement;

            if (getClosestElement(relatedTarget, '.blur-wrapper') != null) {
                e.preventDefault();
            } else {
                onBlur(e);
            }
        }
    }

    let formatOptionLabelImpl: any = undefined;
    if (renderMenuItemChildren) {
        formatOptionLabelImpl = function formatOptionLabelImpl(option: any, labelMeta: LabelMeta) {
            return renderMenuItemChildren(option, labelMeta);
        }
    }

    const defaultIsOptionSelected = (o: any, options: any) => o === props.value || (options.length && options.includes(o));

    //use react-window for real big lists
    let overrideComponents: any = undefined;
    if (props.options && props.options.length >= 300) {
        overrideComponents = { MenuList };
    }

    const handleChange = (selected: any, actionMeta?: string | any ) => {
        if (onChange) {
            onChange(selected, actionMeta)
        }

        if (!disableDirtyTracking) {
            context.setDirty(true);
        }
    }

    const typeAhead = <div tabIndex={-1} onBlur={wrapperOnBlur} className="blur-wrapper">
        <Select
            name={name}
            isClearable
            escapeClearsValue={escapeClearsValue || true}
            backspaceRemovesValue={backspaceRemovesValue || true}
            placeholder={props.placeholder || "< Select >"}
            getOptionLabel={getOptionLabelFn}
            getOptionValue={getOptionValueFn}
            isOptionSelected={props.isOptionSelected || defaultIsOptionSelected}
            components={overrideComponents}
            formatOptionLabel={formatOptionLabelImpl}
            filterOption={createFilter({ ignoreAccents: false })}
            onChange={handleChange}
            isMulti={multiple || false}
            {...otherProps}
        />
    </div>

    return (
        <FormGroup controlId={props.key || props.id}>
            <ControlLabel>{props.label}</ControlLabel>
            {typeAhead}
        </FormGroup>
    );
}

(Typeahead as any).displayName = 'Typeahead';